import { ReactNode } from "react";
import { UUID } from "@bitory-gmbh/nidavellir";
import t from "@/lang/lang";
import {
  updateAirMicrobialSampleSetCache,
  useSamplesAirMicrobialSampleSetListQuery,
  useSamplesAirMicrobialSampleSetShowQuery,
  useSamplesAirMicrobialSampleSetUpdateReferenceMutation,
} from "@/services/backend/samples/api/air-microbial-api";
import {
  AirMicrobialSampleSet,
  airMicrobialReferenceCandidates,
  airMicrobialSetLabel,
} from "@/services/backend/samples/types/air-microbial-sample";
import { useOptimisticDebouncedMutation } from "@/shared/lib/debounce/use-optimistic-debounced-mutation";
import { FilterMode } from "@/shared/lib/filter/filter";
import { Field, FieldLabel } from "@/shared/components/ui/field";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";

/**
 * NO_REFERENCE is the select value of an air microbial set compared against
 * no other set. A select item cannot carry an empty value.
 */
const NO_REFERENCE = "none";

/**
 * useAirMicrobialReference offers the sets the air microbial set may be
 * compared against and saves the choice. The candidates are the air
 * microbial sets of the documents, e.g. of the whole work order, so one
 * outdoor air sample can be the reference of the supply air of many air
 * handling units. Which sets are offered is decided here, the backend only
 * rejects chained references.
 *
 * The current reference is always offered, even if it belongs to another
 * document, so it can be shown and removed.
 */
export function useAirMicrobialReference({
  set,
  companyId,
  documentIds,
}: {
  set: AirMicrobialSampleSet;
  companyId: UUID;
  documentIds: string[];
}) {
  const { data: list } = useSamplesAirMicrobialSampleSetListQuery(
    {
      company: { active: true, values: [companyId] },
      document: { active: true, mode: FilterMode.Or, values: documentIds },
    },
    { skip: !companyId || documentIds.length === 0 },
  );

  const candidates = airMicrobialReferenceCandidates(
    list?.sampleSets ?? [],
    set,
  );
  const referenceOutsideDocuments =
    !!set.referenceSetId &&
    !candidates.some((c) => c.id === set.referenceSetId);

  const [setReference] = useOptimisticDebouncedMutation(
    { id: set.id, referenceSetId: set.referenceSetId },
    updateAirMicrobialSampleSetCache,
    useSamplesAirMicrobialSampleSetUpdateReferenceMutation(),
    // The set is an entity of its own, its errors must not end up among
    // the errors of the document showing it. A rejected reference is
    // shown as a toast.
    { writeToErrValidationFailedContext: false },
  );

  const items: { value: string; label: ReactNode }[] = [
    { value: NO_REFERENCE, label: t("Keine Vergleichsprobe") },
    ...candidates.map((c) => ({ value: c.id, label: airMicrobialSetLabel(c) })),
  ];
  if (referenceOutsideDocuments) {
    items.push({
      value: set.referenceSetId!,
      label: <AirMicrobialSetLabel id={set.referenceSetId!} />,
    });
  }

  return {
    /** Whether there is anything to choose or to show. */
    hasOptions: items.length > 1,
    items,
    value: set.referenceSetId ?? NO_REFERENCE,
    onValueChange: (value: string) => {
      const referenceSetId = value === NO_REFERENCE ? null : value;
      setReference({ id: set.id, referenceSetId }, { ...set, referenceSetId });
    },
  };
}

/** AirMicrobialSetLabel names an air microbial set loaded by its id. */
function AirMicrobialSetLabel({ id }: { id: string }) {
  const { data } = useSamplesAirMicrobialSampleSetShowQuery({ id });

  if (!data) return id;

  return airMicrobialSetLabel(data);
}

/**
 * AirMicrobialReferenceField picks the set the air microbial set is
 * compared against, see {@link useAirMicrobialReference}.
 */
export function AirMicrobialReferenceField({
  set,
  companyId,
  documentIds,
  disabled,
}: {
  set: AirMicrobialSampleSet;
  companyId: UUID;
  documentIds: string[];
  disabled?: boolean;
}) {
  const reference = useAirMicrobialReference({ set, companyId, documentIds });

  return (
    <Field>
      <FieldLabel>{t("Vergleichsprobe")}</FieldLabel>
      <Select
        value={reference.value}
        onValueChange={reference.onValueChange}
        disabled={disabled}
      >
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            {reference.items.map((item) => (
              <SelectItem key={item.value} value={item.value}>
                {item.label}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
    </Field>
  );
}
